import React from 'react'
import { Tag } from 'antd'
import { VideoCameraOutlined } from '@ant-design/icons'
import type { AppointmentCardProps } from '../types'
import { STATUS_CONFIG, TYPE_LABELS } from '../types'
import { formatTime, formatDuration } from '../utils/locale'
import { parseEventDate } from '../utils/time'

export const AppointmentCard: React.FC<AppointmentCardProps> = ({
  event,
  locale,
  compact = false,
  draggable = false,
  resizable = false,
  selected = false,
  onClick,
  onDragStart,
  onResizeStart,
}) => {
  const statusConfig = STATUS_CONFIG[event.status]
  const typeLabel = TYPE_LABELS[event.type]
  const startTime = parseEventDate(event.start)
  const isVideo = !!event.roomUrl

  const cardStyle: React.CSSProperties = {
    backgroundColor: statusConfig.bgColor,
    borderLeft: `4px solid ${statusConfig.borderColor}`,
    color: statusConfig.textColor,
    padding: compact ? '4px 8px' : '8px 12px',
    borderRadius: '6px',
    cursor: draggable ? 'grab' : onClick ? 'pointer' : 'default',
    overflow: 'hidden',
    height: '100%',
    transition: 'transform 0.1s, box-shadow 0.1s',
    ...(selected && {
      border: '2px solid var(--calendar-event-selected-border, #3b82f6)',
      boxShadow: '0 0 0 3px var(--calendar-event-selected-shadow, rgba(59, 130, 246, 0.3))',
    }),
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onClick?.()
  }

  const handleDragStart = (e: React.DragEvent) => {
    if (draggable) {
      e.dataTransfer.effectAllowed = 'move'
      onDragStart?.(e)
    }
  }

  if (compact) {
    return (
      <div
        style={cardStyle}
        onClick={handleClick}
        draggable={draggable}
        onDragStart={handleDragStart}
        className={`appointment-card appointment-card--compact${selected ? ' selected' : ''}`}
      >
        <div style={{
          fontSize: '11px',
          fontWeight: 600,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}>
          {isVideo && <VideoCameraOutlined style={{ fontSize: '10px' }} />}
          <span>{event.patient.name}</span>
        </div>
      </div>
    )
  }

  return (
    <div
      style={cardStyle}
      onClick={handleClick}
      draggable={draggable}
      onDragStart={handleDragStart}
      className={`appointment-card${selected ? ' selected' : ''}`}
    >
      {/* Time */}
      <div style={{
        fontSize: '11px',
        fontWeight: 600,
        opacity: 0.8,
        marginBottom: '2px',
      }}>
        {formatTime(startTime)} - {formatDuration(event.duration, locale)}
      </div>

      {/* Type with video indicator */}
      <div style={{
        fontSize: '13px',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
      }}>
        {isVideo && (
          <VideoCameraOutlined style={{ fontSize: '12px', flexShrink: 0 }} />
        )}
        <span>{locale === 'sr' ? typeLabel.labelSr : typeLabel.label}</span>
      </div>

      {/* Patient name */}
      <div style={{
        fontSize: '12px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        marginTop: '2px',
      }}>
        {event.patient.name}
      </div>

      {/* Doctor name */}
      <div style={{
        fontSize: '11px',
        opacity: 0.7,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}>
        {event.doctor.title} {event.doctor.name}
      </div>

      {/* Status tag - only show if not confirmed (unusual status) */}
      {event.status !== 'CONFIRMED' && event.status !== 'SCHEDULED' && (
        <Tag
          color={statusConfig.borderColor}
          style={{
            marginTop: '4px',
            fontSize: '10px',
            padding: '0 4px',
            lineHeight: '16px',
          }}
        >
          {locale === 'sr' ? statusConfig.labelSr : statusConfig.label}
        </Tag>
      )}

      {/* Resize Handle */}
      {resizable && (
        <div
          onMouseDown={(e) => {
            e.stopPropagation()
            onResizeStart?.(e)
          }}
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '6px',
            cursor: 'ns-resize',
            zIndex: 10,
            backgroundColor: 'transparent',
          }}
          className="appointment-card__resize-handle"
        />
      )}
    </div>
  )
}
